import {
  resultToSuccess,
  resultToFailed,
  optionsFor,
  safeFetch
} from '../Helper'
import stockApi, { addTokenHeader } from '../StockApi'
import { unformatNumberBR } from '../../utils'
import { batchMapper } from '../../utils/mappers/batchMapper'
import { mapReceivement } from '../../utils/mappers/mapReceivement'
import { formatDateIso } from 'src/utils/formatDate'

const ReceivementTypes = {
  MERCEARIA: 'MERCEARIA'
}

export const getReceivement = async (receivementId) => {
  try {
    const response = await stockApi.get(
      `/v2/recebimento/${receivementId}`,
      await addTokenHeader()
    )
    return resultToSuccess(response)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getReceivementList = async (
  badge,
  page,
  perPage,
  invoiceNumber,
  arId,
  idFC,
  fcActive = null
) => {
  try {
    const response = await stockApi.get(`/v2/recebimento/list`, {
      params: {
        page: page,
        per_page: perPage,
        invoice_number: invoiceNumber,
        ar_id: arId,
        id_fulfillment_center: idFC,
        fc_active: fcActive
      },
      headers: {
        'user-barcode': badge
      }
    })

    response.data.data = response.data.data.map(mapReceivement)
    return resultToSuccess(response)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getReceivementBatches = async (badge, receivementId) => {
  try {
    const response = await stockApi.get(
      `/v2/recebimento/${receivementId}/batches`,
      optionsFor(badge)
    )

    if(response.data?.has_frozen_items){
      return resultToSuccess({...response, data: {has_frozen_items: true}})
    }

    response.data = response.data.data.map((batch) => batchMapper(batch))
    return resultToSuccess(response)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const sendReceivementToManagerCheck = async (badge, receivementId) => {
  try {
    const response = await stockApi.post(
      `/v2/recebimento/${receivementId}/manager-check`,
      null,
      optionsFor(badge)
    )
    return resultToSuccess(response)
  } catch (e) {
    return resultToFailed(e)
  }
}

export async function createBatch(
  badge,
  receivementId,
  modelId,
  batch
) {
  const batchCreateObject = {
    id_modelo: modelId,
    cod_barras: String(batch.codigoDeBarras),
    quantidade: batch.total ? unformatNumberBR(batch.total) : batch.weight,
    data_validade: formatDateIso(batch.validade),
    id_recebimento: receivementId,
    cracha_usuario: badge,
    id_fornecedor: 1,
    tipo_recebimento: ReceivementTypes.MERCEARIA,
    tipo_avaria: batch.tipoAvarias,
    quantidade_avaria: batch.quantidadeAvarias,
    descricao_personalizada: batch.descricao,
    tipo_unidade: batch.unidade,
    temperatura_item: batch.temperaturaItem,
  }

  const response = await stockApi.post(
    '/v2/recebimento/lote',
    batchCreateObject,
    optionsFor(badge)
  )

  return response.data.data
}

export const getTiposAvaria = async (badge) => {
  const response = await stockApi.get(
    '/v2/recebimento/tipo-avaria',
    optionsFor(badge)
  )
  const body = response.data
  const tipos = body.data
  return tipos
}

export const deleteBatch = async (badge, batchId) => {
  return stockApi.delete(`/v2/recebimento/lote/${batchId}`, optionsFor(badge))
}

export const validateSupervisorBadge = async (badge) => {
  try {
    const resultSuccess = await stockApi.post(
      '/v2/recebimento/supervisao/validar',
      null,
      optionsFor(badge)
    )
    return resultToSuccess(resultSuccess)
  } catch (resultFailed) {
    return resultToFailed(resultFailed)
  }
}

export const confirmWeightDivergence = async (badge) => {
  try {
    const resultSuccess = await stockApi.post(
      '/v2/recebimento/fractioning/confirm-divergence',
      null,
      optionsFor(badge)
    )
    return resultToSuccess(resultSuccess)
  } catch (resultFailed) {
    return resultToFailed(resultFailed)
  }
}

export const setReceivementSupervisor = async (badge, receivementId) => {
  try {
    const resultSuccess = await stockApi.post(
      `/v2/recebimento/${receivementId}/supervisor`,
      null,
      optionsFor(badge)
    )
    return resultToSuccess(resultSuccess)
  } catch (resultFailed) {
    return resultToFailed(resultFailed)
  }
}

export const getReceivementFromQueue = async (dockId) => {
  try {
    const result = await stockApi.get(
      `/v2/queue/receivement/next?dock_id=${dockId}`,
      await addTokenHeader()
    )
    result.data = mapReceivement(result.data)
    return resultToSuccess(result)
  } catch (resultFailed) {
    return resultToFailed(resultFailed)
  }
}

export const sendReceivementBackToQueue = async (badge, receivementId, status) => {
  try {
    const result = await stockApi.post(
      `/v2/queue/receivement/${receivementId}/back-to-queue`,
      {
        status
      },
      optionsFor(badge)
    )
    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getReceivementByAttendancePassword = async (
  badge,
  attendancePassword
) => {
  try {
    const result = await stockApi.get('/v2/recebimento/active', {
      params: {
        attendance_password: attendancePassword
      },
      headers: {
        'user-barcode': badge
      }
    })
    return resultToSuccess(result)
  } catch (resultFailed) {
    return resultToFailed(resultToFailed)
  }
}

export const startReceivement = async (
  badge,
  receivementId,
  attendancePassword
) => {
  try {
    const result = await stockApi.post(
      `/v2/recebimento/${receivementId}/start`,
      {
        badge: badge,
        attendance_password: attendancePassword
      },
      await addTokenHeader()
    )
    return resultToSuccess(result)
  } catch (resultFailed) {
    return resultToFailed(resultFailed)
  }
}

export const getSalesAdjustmentResult = async (badge, receivementId) => {
  try {
    const response = await stockApi.get(
      `/v2/recebimento/${receivementId}/sales-adjustment-result`,
      optionsFor(badge)
    )
    response.data = response.data.data
    return resultToSuccess(response)
  } catch (resultFailed) {
    return resultToFailed(resultToFailed)
  }
}

export const setReceivementProdutoAddedToErp = async (
  badge,
  productDivergenceId
) => {
  try {
    const result = await stockApi.patch(
      `/v2/recebimento/product-divergence/${productDivergenceId}/set-added-to-erp`,
      {},
      optionsFor(badge)
    )
    return resultToSuccess(result)
  } catch (resultFailed) {
    return resultToFailed(resultToFailed)
  }
}

export const proceedToNextStep = async (badge, receivementId) => {
  return await safeFetch(() =>
    stockApi.post(
      `/v2/recebimento/${receivementId}/proceed-to-next-step`,
      {},
      optionsFor(badge)
    )
  )
}

export const getReceivements = async (
  dateFrom,
  dateTo,
  supplierValue,
  fcId
) => {
  try {
    const authHeader = await addTokenHeader()    
    const responseSuccess = await stockApi.get('/recebimento/recebimentos', {
      ...authHeader,
      params: {
        dateFrom,
        dateTo,
        fornecedor: supplierValue,
        id_fulfillment_center: fcId
      }
    })
    return resultToSuccess(responseSuccess)
  } catch (responseFailed) {
    throw resultToFailed(responseFailed)
  }
}

export const getInvoiceToQueue = async (invoiceAccessKey, fcId, arTypeId, externalOrderCode) => {
  try {
    const header = await addTokenHeader()
    let url = `/v2/invoice/${invoiceAccessKey}/queue-detail?fulfillmentCenterId=${fcId}&arTypeId=${arTypeId}`

    if (externalOrderCode) {
      url += `&externalOrderCode=${externalOrderCode}`
    }

    const result = await stockApi.get(url, header)

    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getFractionableBatches = async ({
  fulfillmentCenterId,
  page,
  perPage,
  order,
  orderBy
}) => {
  try {
    const params = { fulfillment_center_id: fulfillmentCenterId }

    if (page && perPage) {
      params.page = page
      params.per_page = perPage

      if (order && orderBy) {
        params.order = order
        params.order_by = orderBy
      }
    }

    const result = await stockApi.get(`/receivement/fraction/batches`, { params })

    const batches = Array.isArray(result.data)
      ? result.data
      : result.data?.batches ?? []

    const paginationRaw = Array.isArray(result.data)
      ? undefined
      : result.data?.pagination

    result.data = {
      batches,
      pagination: {
        currentPage: paginationRaw?.current_page ?? page,
        perPage: paginationRaw?.per_page ?? perPage,
        totalItems: paginationRaw?.total_items ?? batches.length,
        totalPages: paginationRaw?.total_pages ?? 1
      }
    }

    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getFractionableBatch = async ({
  batchId,
  fulfillmentCenterId
}) => {
  try {
    const result = await stockApi.get(
      `/receivement/fraction/batches/${batchId}`,
      {
        params: { fulfillment_center_id: fulfillmentCenterId }
      }
    )

    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getBatchFraction = async (idBatch) => {
  try {
    const result = await stockApi.get(`/recebimento/fracoes/lote/${idBatch}`)
    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const getNotReleasedReceivements = async (fcId) => {
  try {
    const header = await addTokenHeader()

    const result = await stockApi.get('/v2/recebimento/not-released', {
      ...header,
      params: {
        id_fulfillment_center: fcId
      }
    })
    return resultToSuccess(result.data)
  } catch (e) {
    return resultToFailed(e)
  }
}

export const saveTruckTemperature = async (badge, receivementId, temperatureData) => {
  try {
    const result = await stockApi.post(
      `/v2/recebimento/${receivementId}/batches/truck-temperature`,
      {
        temperature: temperatureData.temperature,
        refrigeration_on: temperatureData.refrigerationOn
      },
      optionsFor(badge)
    )
    return resultToSuccess(result)
  } catch (e) {
    return resultToFailed(e)
  }
}
